import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Layers } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { createMockTest } from "@/api/lms/mock-tests";
import { useQuery } from "@tanstack/react-query";
import { examConfigApi, ExamConfig, ExamLevel } from "@/api/admin/examConfig";

type MockCreationStep = "selectType" | "selectLevel" | "confirm" | "creating";

export default function CreateMockTestPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const { data: activeConfigs = [], isLoading: isLoadingConfigs } = useQuery({
    queryKey: ["activeExamConfigs"],
    queryFn: () => examConfigApi.getActive(),
  });

  const [creationStep, setCreationStep] = useState<MockCreationStep>("selectType");
  const [selectedConfig, setSelectedConfig] = useState<ExamConfig | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<ExamLevel | null>(null);

  useEffect(() => {
    if (creationStep !== "creating" || !selectedConfig) return;

    let active = true;

    async function autoCreate() {
      try {
        const titleLevel = selectedLevel ? ` ${selectedLevel.code}` : "";
        const dto = {
          examType: selectedConfig!.code,
          level: selectedLevel?.code || undefined,
          title: `New ${selectedConfig!.name}${titleLevel} Mock Test`,
          duration: selectedConfig!.code === "JFT_BASIC" ? 60 : 100, // Default duration
          totalMarks: selectedConfig!.code === "JFT_BASIC" ? 250 : 180, // Default marks
          passingMarks: selectedConfig!.code === "JFT_BASIC" ? 200 : 90, // Default pass marks
        };

        const created = await createMockTest(dto);
        if (!active) return;
        toast.success(`${selectedConfig!.name} mock test shell created`);
        navigate(`/mock-tests/${created.id}/edit`, { replace: true });
      } catch (e: unknown) {
        if (!active) return;
        toast.error(
          typeof e === "object" && e && "message" in e
            ? String((e as { message: unknown }).message)
            : "Could not create mock test"
        );
        setCreationStep("selectType");
      }
    }

    void autoCreate();

    return () => {
      active = false;
    };
  }, [creationStep, navigate, selectedConfig, selectedLevel]);

  if (!user) return null;

  if (creationStep === "creating") {
    return (
      <DashboardLayout>
        <div className="p-10 flex flex-col justify-center items-center gap-3 text-muted-foreground min-h-[400px]">
          <Loader2 className="animate-spin text-primary animate-duration-1000" size={32} />
          <p className="text-sm font-medium animate-pulse">
            Initializing {selectedConfig?.name} mock test editor...
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-6 font-sans">
        <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2 h-8 px-2" asChild>
          <Link to="/mock-tests">
            <ArrowLeft className="size-4 mr-1.5 inline" aria-hidden />
            Mock tests
          </Link>
        </Button>

        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Create a mock test</h1>
          <p className="text-muted-foreground text-sm">
            Select the examination model as the foundation for your mock test practice booklet.
          </p>
        </div>

        {isLoadingConfigs && (
          <div className="flex justify-center p-10">
            <Loader2 className="animate-spin text-muted-foreground" size={24} />
          </div>
        )}

        {creationStep === "selectType" && !isLoadingConfigs && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {activeConfigs.map((config) => (
              <KCard
                key={config.id}
                hoverable
                onClick={() => {
                  setSelectedConfig(config);
                  if (config.levels && config.levels.length > 0) {
                    setSelectedLevel(config.levels[0]);
                    setCreationStep("selectLevel");
                  } else {
                    setSelectedLevel(null);
                    setCreationStep("confirm");
                  }
                }}
                className="p-6 flex flex-col gap-4 border-border hover:border-primary/45 transition-all cursor-pointer h-full"
              >
                <div className="rounded-xl bg-primary/10 w-12 h-12 flex items-center justify-center text-primary">
                  <Layers className="size-6" />
                </div>
                <div className="space-y-1.5 flex-1">
                  <h2 className="font-bold text-lg text-foreground">{config.name}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {config.description || `Create a new ${config.name} mock test.`}
                  </p>
                  <p className="text-xs font-semibold text-muted-foreground mt-2">
                    {config.passCriteria === 'SECTIONAL_REQUIRED' ? 'Sectional Passing Required' : 'Overall Passing Only'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full mt-4 font-bold border-stone-300 text-stone-700 dark:border-stone-700 dark:text-stone-300 hover:bg-stone-50"
                >
                  Select {config.code}
                </Button>
              </KCard>
            ))}
            
            {activeConfigs.length === 0 && (
              <div className="col-span-full p-8 text-center text-muted-foreground bg-muted/20 rounded-xl border border-border">
                No active exam configurations found. Please create one in the Admin settings first.
              </div>
            )}
          </div>
        )}

        {creationStep === "selectLevel" && selectedConfig && (
          <div className="max-w-md bg-card border border-border/80 rounded-2xl p-6 space-y-6">
            <div className="space-y-2">
              <h2 className="text-lg font-bold text-foreground">Select {selectedConfig.name} Level</h2>
              <p className="text-xs text-muted-foreground">Choose the targeted grade for the new mock test shell.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {selectedConfig.levels?.sort((a, b) => a.sortOrder - b.sortOrder).map(lvl => (
                <button
                  key={lvl.id}
                  type="button"
                  onClick={() => setSelectedLevel(lvl)}
                  className={`h-11 rounded-xl text-sm font-bold border transition-all ${
                    selectedLevel?.id === lvl.id
                      ? "bg-slate-900 border-slate-950 text-white dark:bg-slate-100 dark:text-slate-900"
                      : "border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {lvl.code}
                </button>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                onClick={() => setCreationStep("selectType")}
              >
                Back
              </Button>
              <Button
                size="sm"
                className="flex-1 text-xs font-bold"
                onClick={() => setCreationStep("creating")}
              >
                Create Mock Test
              </Button>
            </div>
          </div>
        )}

        {creationStep === "confirm" && selectedConfig && (
          <div className="max-w-md bg-card border border-border/80 rounded-2xl p-6 space-y-5">
            <div className="space-y-2">
              <h2 className="text-lg font-bold text-foreground">Create {selectedConfig.name} Test</h2>
              <p className="text-xs text-muted-foreground">The test will be initialized with the following parameters:</p>
            </div>
            <div className="bg-muted/40 p-4 border rounded-xl text-xs space-y-2.5 text-foreground font-sans">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Exam Type:</span>
                <span className="font-bold">{selectedConfig.name}</span>
              </div>
              <div className="flex justify-between border-t border-border/60 pt-2">
                <span className="text-muted-foreground">Passing Rule:</span>
                <span className="font-bold">{selectedConfig.passCriteria === 'SECTIONAL_REQUIRED' ? 'Overall & Sectional' : 'Overall Only'}</span>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                onClick={() => setCreationStep("selectType")}
              >
                Back
              </Button>
              <Button
                size="sm"
                className="flex-1 text-xs font-bold bg-slate-900 hover:bg-slate-850 text-white"
                onClick={() => setCreationStep("creating")}
              >
                Create Mock Test
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
